import { FC, useEffect, useMemo } from 'react';
import {
  SearchPageFragment,
  TermCategory,
  TermCategoryFragment,
} from '../../../generated/types';
import {
  Box,
  Button,
  Container,
  Divider,
  Heading,
  Icon,
  Modal,
  ModalOverlay,
  Tab,
  TabList,
  TabPanels,
  Flex,
  TabPanel,
  Tabs,
  useDisclosure,
} from '@chakra-ui/react';
import { Metatags } from '../../Layout/Metatags';
import { ParagraphRenderer } from '../../Paragraph/ParagraphRenderer';
import { queryToFilters } from '../../../utils/filters';
import { useRouter } from 'next/router';
import { RiEqualizerLine } from '@react-icons/all-files/ri/RiEqualizerLine';
import { FilterForm } from '../../Forms/FilterForm';
import { LocationSearch } from '../../Common/LocationSearch';
import { SearchContext } from '../../../context/SearchContext';
import { AskLocationModal } from './AskLocationModal';

interface SearchPageProps {
  data: SearchPageFragment;
}

export const SearchPage: FC<SearchPageProps> = ({ data }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const router = useRouter();
  const {
    isOpen: isOpenAskLocation,
    onOpen: onOpenAskLocation,
    onClose: onCloseAskLocation,
  } = useDisclosure({
    defaultIsOpen:
      data.askForLocation && !router.query.lat && !router.query.lng,
  });

  const initialFilter = useMemo(() => {
    const parsed = queryToFilters(router.query.filters as string);
    const filters: number[] = [];

    if (parsed) {
      parsed.forEach((group) => {
        group.conditions?.forEach((condition) => {
          if (condition.operator === 'IN') {
            condition.value.split(',').forEach((value) => {
              filters.push(parseInt(value, 10));
            });
          } else {
            filters.push(parseInt(condition.value, 10));
          }
        });
      });
    }

    return filters;
  }, [router.query]);

  const activeFilter = useMemo(() => {
    const activeFilter: TermCategoryFragment[] = [];

    initialFilter.forEach((filterId) => {
      data.searchMoreFilter.forEach((moreFilterParagraph) => {
        if (filterId === moreFilterParagraph.category.id) {
          activeFilter.push(moreFilterParagraph.category);
        }

        moreFilterParagraph.subCategories.forEach((subCategory) => {
          if (filterId === subCategory.id) {
            activeFilter.push(subCategory);
          }
        });
      });
    });

    return activeFilter;
  }, [initialFilter, data]);

  return (
    <>
      <AskLocationModal
        data={data}
        isOpen={isOpenAskLocation}
        onClose={onCloseAskLocation}
      />
      <Metatags metatags={data.metatags} />
      <Box bg="accent" py={9} color="primary">
        <Container maxW="container.main" textAlign="center">
          <Heading fontWeight={500} as="h1">
            {data.title}
          </Heading>
          <Divider mt={2} mb={6} />
          <Box
            fontSize="lg"
            dangerouslySetInnerHTML={{
              __html: data.description,
            }}
          />
          <Box mt={9} mb={-16}>
            <Box color="primary">
              <Flex
                flexDir={{
                  base: 'column',
                  md: 'row',
                }}
                py={4}
                px={6}
                bg="white"
                borderRadius="sm"
                shadow="lg"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box
                  width={{
                    base: '100%',
                    md: 'auto',
                  }}
                  mb={{
                    base: 4,
                    md: 0,
                  }}
                  mr={{
                    base: 0,
                    md: !data.location ? 8 : 0,
                  }}
                >
                  <Button
                    width={{
                      base: '100%',
                      md: 'auto',
                    }}
                    variant="outline"
                    borderRadius="sm"
                    onClick={onOpen}
                  >
                    <Icon mr={1} as={RiEqualizerLine} />
                    Mehr Filter{' '}
                    {initialFilter.length > 0 && `(${initialFilter.length})`}
                  </Button>
                </Box>
                {!data.location && (
                  <LocationSearch
                    value={''}
                    onInputChange={() => {}}
                    onSubmit={() => {}}
                    width="100%"
                  />
                )}
              </Flex>
            </Box>
          </Box>
        </Container>
      </Box>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <FilterForm
          data={data}
          initialFilter={initialFilter}
          onSubmitted={onClose}
        />
      </Modal>
      <SearchContext.Provider
        value={{
          activeFilter,
          searchMoreFilter: data.searchMoreFilter,
        }}
      >
        <ParagraphRenderer
          paragraphs={data.content}
          wrapperProps={{
            py: 0,
          }}
          getWrapperForParagraph={(paragraphType) =>
            paragraphType !== 'ParagraphSearchResult'
              ? 'containerSmall'
              : 'container'
          }
        />
      </SearchContext.Provider>
    </>
  );
};
